/**
 * Frontend Authentication Service - Calls backend for all auth operations
 */

import { PKCEUtils } from './PKCEUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api';

class AuthService {
  constructor() {
    this.baseUrl = `${API_BASE_URL}/auth`;
    this.codeVerifier = null;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    
    const config = {
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Origin': window.location.origin,
        ...options.headers
      },
      ...options
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `HTTP error! status: ${response.status}`);
      }
      
      return data;
    } catch (error) {
      console.error(`Auth request failed: ${endpoint}`, error);
      throw error;
    }
  }

  /**
   * Get login URL from backend (server-side flow)
   */
  async getLoginUrl() {
    try {
      const response = await this.request('/login');
      return response.loginUrl;
    } catch (error) {
      console.error('Failed to get login URL:', error);
      throw error;
    }
  }

  /**
   * Verify token with backend
   */
  async verifyToken(token) {
    return this.request('/verify', {
      method: 'POST',
      body: JSON.stringify({ token })
    });
  }

  /**
   * Get current user from backend
   */
  async getCurrentUser() {
    return this.request('/user');
  }

  /**
   * Logout user
   */
  async logout() {
    return this.request('/logout', {
      method: 'POST'
    });
  }

  /**
   * Refresh expired token
   */
  async refreshToken() {
    try {
      const oldToken = this.getToken() || this.getCookie('auth_token');
      if (!oldToken) {
        throw new Error('No token to refresh');
      }

      const response = await this.request('/refresh', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${oldToken}`
        }
      });

      if (response.success && response.token) {
        // Store the new token
        this.setToken(response.token);
        return response;
      } else {
        throw new Error(response.error || 'Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  }

  /**
   * Handle auth callback (extract code from URL and exchange for token)
   */
  async handleAuthCallback() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      const returnedState = urlParams.get('state');

      if (error) {
        console.error('Auth error:', decodeURIComponent(errorDescription || error));
        throw new Error(decodeURIComponent(errorDescription || error));
      }

      // Verify state parameter
      const savedState = sessionStorage.getItem('authState');
      if (!returnedState || returnedState !== savedState) {
        throw new Error('Invalid state parameter');
      }

      if (code) {
        // Get code verifier from session storage
        const codeVerifier = sessionStorage.getItem('pkceVerifier');
        if (!codeVerifier) {
          throw new Error('No PKCE verifier found in session storage');
        }

      try {
        // Exchange code for token
        const redirectUri = `${window.location.origin}/auth/success`;
        const response = await this.request('/callback', {
          method: 'POST',
          body: JSON.stringify({
            code,
            redirect_uri: redirectUri,
            code_verifier: codeVerifier,
            // Note: client_id is NOT needed here - backend already has it
          })
        });

        if (response.token) {
          this.setToken(response.token);
          // Clean up session storage
          sessionStorage.removeItem('pkceVerifier');
          sessionStorage.removeItem('authState');
          // Clean up URL parameters
          window.history.replaceState({}, document.title, window.location.origin);
          return response.token;
        } else {
          throw new Error('No token received from server');
        }
      } catch (error) {
        console.error('Token exchange failed:', error);
        throw error;
      }
    }
    return null;
  } catch (error) {
    console.error('Auth callback failed:', error);
    throw error;
  }
  }

  /**
   * Store token in localStorage (fallback, cookies are primary)
   */
  setToken(token) {
    localStorage.setItem('authToken', token);
  }

  /**
   * Get token from localStorage (fallback, cookies are primary)
   */
  getToken() {
    return localStorage.getItem('authToken');
  }

  /**
   * Remove token from localStorage
   */
  removeToken() {
    localStorage.removeItem('authToken');
  }

  /**
   * Get cookie value by name
   */
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  /**
   * Get user info from cookies
   */
  getUserFromCookies() {
    const email = this.getCookie('user_email');
    const name = this.getCookie('user_name');
    const company = this.getCookie('user_company');
    const tenant = this.getCookie('user_tenant');
    
    if (email) {
      return { email, name, company, tenant };
    }
    return null;
  }

  /**
   * Check if user is authenticated (check cookies first, then localStorage)
   */
  isAuthenticated() {
    return !!(this.getCookie('auth_token') || this.getToken());
  }
}

export const authService = new AuthService();